// src/components/Designation/Designation.jsx
// Main component for Designation Management page

import React, { useState, useEffect } from 'react';

import designationService from '../../services/designationService';

import DesignationTable from './DesignationTable';
import DesignationModal from './DesignationModal';
import EditDesignationModal from './EditDesignationModal';
import ConfirmDialog from "../common/ConfirmDialog"; 
import { showNotification } from '../../utils/notification';
import { PlusCircle } from 'lucide-react';

import '../Inventory/inventory.css'; // Use same CSS as inventory

/**
 * Designation Component
 * Main page component that manages designations
 * Handles CRUD operations and filtering
 */
const Designation = ({ toggleSidebar }) => {
  // ========== STATE MANAGEMENT ==========

  // All designations from the backend
  const [designations, setDesignations] = useState([]);

  // Filtered designations (after applying search/filters)
  const [filteredDesignations, setFilteredDesignations] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Saving state (for add operations)
  const [isSaving, setIsSaving] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Currently selected designation for editing (null for add mode)
  const [selectedDesignation, setSelectedDesignation] = useState(null);

  // Filter values
  const [filters, setFilters] = useState({
    search: '',
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
//deleting
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [designationToDelete, setDesignationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (designation) => {
  setDesignationToDelete(designation);
  setIsConfirmOpen(true);
};

  // ========== LOAD DATA ON COMPONENT MOUNT ==========

  /**
   * Load all designations from the backend when component first renders
   * This runs once when the component mounts
   */
  useEffect(() => {
    loadDesignations();
  }, [page]); // Runs on mount AND when page changes

  /**
   * Apply filters whenever designations or filter values change
   */
  useEffect(() => {
    applyFilters();
  }, [designations, filters]); // Run whenever designations or filters change

  // ========== DATA LOADING ==========

  /**
   * Fetch all designations from the backend
   */
  const loadDesignations = async () => {
    try {
      setIsLoading(true);

      // Call the API service
      const data = await designationService.getAll(page, limit);
      setHasNextPage(data.length === limit);
      
      // Estimate total pages
      if (data.length < limit) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1);
      }

      // Update state with fetched data
      setDesignations(data);

      // Show success message
      showNotification(`Loaded ${data.length} designations`, 'success');
    } catch (error) {
      console.error('Error loading designations:', error);
      showNotification(`Failed to load designations: ${error.message}`, 'error');

      // Set empty array on error
      setDesignations([])
    } finally {
      // Always stop loading, whether successful or not
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========

  /**
   * Apply current filters to the designation list
   */
  const applyFilters = () => {
    const safeDesignations = Array.isArray(designations) ? designations : [];
    let filtered = [...safeDesignations];

    // Filter by search term (searches name)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();

      filtered = filtered.filter((desig) => {
        const name = desig.name?.toLowerCase() || '';
        const description = desig.description?.toLowerCase() || '';
        return name.includes(searchLower) || description.includes(searchLower);
      });
    }

    setFilteredDesignations(filtered);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      search: '',
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========

  /**
   * Open modal for adding new designation
   */
  const handleAddNew = () => {
    setSelectedDesignation(null); // Clear any selected designation
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing existing designation
   */
  const handleEdit = (designation) => {
    setSelectedDesignation(designation);
    setIsEditModalOpen(true);
  };

  /**
   * Close the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedDesignation(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedDesignation(null);
  };

  // ========== CRUD OPERATIONS ==========

  /**
   * Save designations (create list)
   * @param {Object} payload - The form data from the modal
   */
  const handleSave = async (payload) => {
    try {
      setIsSaving(true);

      // Service expects single designation object, wraps it in array internally
      const designationsArray = payload.designations || [payload];
      
      // Create each designation separately
      for (const designation of designationsArray) {
        await designationService.create(designation);
      }

      showNotification(
        `${designationsArray.length} designation(s) added successfully!`,
        'success'
      );

      setPage(1); // reset page
      handleCloseModal(); // close once
      await loadDesignations();
    } catch (error) {
      console.error('Error saving designations:', error);
      showNotification(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Delete a designation
   */
  const confirmDelete = async () => {
  if (!designationToDelete) return;

  try {
    setIsDeleting(true);
    // Use delete method which calls /api/v1/designations/delete/:id
    await designationService.delete(designationToDelete.id);

    showNotification("Designation deleted successfully", "success");

    await loadDesignations(); // refresh table
  } catch (error) {
    console.error("Delete failed:", error);
    showNotification(`Failed to delete: ${error.message}`, "error");
  } finally {
    setIsDeleting(false);
    setIsConfirmOpen(false);
    setDesignationToDelete(null);
  }
};

const cancelDelete = () => {
  setIsConfirmOpen(false);
  setDesignationToDelete(null);
};

  // update single designation
  const handleSaveEdits = async (formData) => {
    try {
      setIsSavingEdit(true);

      const payload = {
        name: formData.name,
        isActive: formData.isActive,
      };

      await designationService.update(selectedDesignation.id, payload);

      showNotification(
        `Designation "${payload.name}" updated successfully!`,
        'success'
      );

      await loadDesignations(); // refresh table
      handleCloseEditModal(); // close modal
    } catch (error) {
      console.error('Edit failed:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSavingEdit(false);
    }
  };

  // ========== PAGINATION HELPERS ==========
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // ========== RENDER ==========

  return (
    <main className="inventory-main-content">
      {/* Page Header */}
      <header className="inventory-content-header">
        <button
          onClick={toggleSidebar}
          className="sidebar-toggle-btn"
          type="button"
        >
          ☰
        </button>
        <h2 className="inventory-content-title">Designation Management</h2>

        <div className="header-actions">
          <button
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
            type="button"
          >
            <PlusCircle size={18} />
            Add New Designation
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          {/* Search Input */}
          <input
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search Designation Name or Description"
            value={filters.search}
            onChange={handleFilterChange}
          />

          {/* Apply Filter Button (filters apply automatically, but kept for UI consistency) */}
          {/* <button
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
            type="button"
          >
            Apply Filter
          </button> */}

          {/* Clear Filters Button */}
          <button
            className="inventory-btn inventory-btn-clear"
            onClick={handleClearFilters}
            type="button"
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <section>
        <DesignationTable
          designations={filteredDesignations}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          isLoading={isLoading}
        />
      </section>

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {page > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {page > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {page > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page - 1)}
            >
              {page - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{page}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </button>
          )}
          
          {hasNextPage && page < totalPages - 1 && (
            <>
              {page < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {page} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      {/* Add Modal */}
      <DesignationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        designation={selectedDesignation}
        isSaving={isSaving}
      />

      {/* Edit Modal */}
      <EditDesignationModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdits}
        designation={selectedDesignation}
        isSaving={isSavingEdit}
      />
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Designation"
        message={`Are you sure you want to delete "${designationToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default Designation;